/* Qué universos del menú están plegados.
 *
 * ── QUÉ RESUELVE ─────────────────────────────────────────────────────────
 *
 * El rail tiene **veinticinco entradas en una columna de 250 px** y nadie
 * trabaja en más de dos sectores. El de portería recorre Tráfico entero —ocho
 * ítems— para llegar a lo suyo, todos los días.
 *
 * ── NO ESCONDE NADA, Y ÉSA ES LA REGLA ───────────────────────────────────
 *
 * Se pliega **a mano y a propósito**, nunca por uso ni por permiso. Un menú
 * que se recorta solo miente por omisión: nadie sabe que esa pantalla existe
 * ni que se puede pedir. Es lo mismo que ya está escrito para las entradas
 * `pronto` —se muestran apagadas en vez de esconderse— y para «Cómo se usa»,
 * que cuenta aparte las pantallas que no se tienen.
 *
 * Y por eso **arranca todo abierto**. Plegar de arranque le esconde pantallas
 * a alguien que no lo pidió; el que quiere el rail corto lo pliega una vez y
 * queda así.
 *
 * ── EL UNIVERSO DE LA PANTALLA ABIERTA NUNCA SE PLIEGA ───────────────────
 *
 * Si se pudiera, alguien plegaría Tráfico, tocaría el Mapa desde otro lado y
 * el menú no marcaría nada: la pantalla abierta no estaría en la lista. Un
 * menú que no dice dónde estás parado se lee como que la navegación se rompió.
 *
 * ── POR QUÉ EN `localStorage` Y NO EN `preferencias` ─────────────────────
 *
 * Las preferencias viven en la base para que sigan a la persona entre el
 * celular y la computadora. Esto es al revés: **es de este navegador**, porque
 * el rail que sobra en una pantalla de 27" es el que hay que plegar en una
 * notebook. Y sobre todo tiene que estar disponible en el primer pintado — con
 * una consulta de por medio, el rail se dibujaría largo y se plegaría solo
 * medio segundo después, que se ve como un error.
 *
 * Las funciones son puras salvo `leer`/`guardar`, que reciben el storage: así
 * la regla se prueba sin navegador.
 */

export const CLAVE = "hub.universos.plegados";

/* Un `Set` y no una lista: la pregunta que se hace veinte veces por render es
 * «¿está éste plegado?». */
export function leer(storage) {
  try {
    const crudo = storage?.getItem(CLAVE);
    const lista = crudo ? JSON.parse(crudo) : [];
    return new Set(Array.isArray(lista) ? lista.filter((x) => typeof x === "string") : []);
  } catch {
    /* Un JSON roto —o un navegador que no deja leer storage— no puede dejar
     * sin menú a nadie: se arranca con todo abierto, que es el estado que no
     * esconde nada. */
    return new Set();
  }
}

export function guardar(plegados, storage) {
  try {
    storage?.setItem(CLAVE, JSON.stringify([...plegados]));
  } catch {
    /* Si no se pudo guardar, el rail sigue funcionando y la elección dura lo
     * que dure la pestaña. Frenar por no poder recordar un menú sería absurdo. */
  }
}

/* Devuelve un `Set` NUEVO: mutarlo no le avisaría a React y el rail no se
 * volvería a dibujar. */
export function alternar(plegados, universo) {
  const s = new Set(plegados);
  if (s.has(universo)) s.delete(universo);
  else s.add(universo);
  return s;
}

/* ¿Se ven los ítems de este universo?
 *
 * El activo gana siempre, incluso plegado a mano: es la única forma de que el
 * menú pueda marcar dónde estás parado.
 */
export function abierto(universo, plegados, universoActivo) {
  if (universo && universo === universoActivo) return true;
  return !plegados?.has(universo);
}

/* Cuántas pantallas tiene un universo, contando sólo las que esa persona
 * puede abrir.
 *
 * VA AL LADO DEL TÍTULO CUANDO ESTÁ PLEGADO, y no es decorativo: sin el
 * número, un universo cerrado y uno vacío se ven exactamente igual, y el que
 * no sabe que Tráfico tiene nueve pantallas no las va a buscar.
 */
export function contar(grupos, universo) {
  return (grupos || [])
    .filter((g) => g.universo === universo)
    .reduce((n, g) => n + (g.items?.length || 0), 0);
}
